import { Button } from "@/components/ui/button"
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  MoreVertical,
  Menu,
  X,
  FileText,
  Layout,
  Quote,
  GitCompare,
  Wrench,
  Smile,
  Users,
} from "lucide-react"
import { useState, useEffect, type ComponentType, type ReactNode } from "react"
import Link from "next/link"
import { useRouter, usePathname, useSearchParams } from "next/navigation"
import { googleSignIn } from '@/app/auth'
import { GoogleIcon } from "@/components/ui/google-icon"
import { Skeleton } from '@/components/ui/skeleton'
import { useAuthContext } from '@/app/firebase/AuthContext'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import logger from '@/components/logger'
import { cn } from '@/lib/utils'
import { useSidebar } from '@/components/sidebar-context'
import { useBrandedReportBootstrap } from '@/components/branded-report/BrandedReportBootstrapContext'
import { SeoPulseSidebarLogoMark } from '@/components/branding/SeoPulseSidebarLogoMark'
import { appendV1ReportFilterQueryToPath } from '@/app/ai-overview-analysis/[id]/lib/v1-report-topbar-utils'
import { SidebarAioReportSwitcher } from '@/components/sidebar-aio-report-switcher'
import {
  fetchUserAioReports,
  type UserAioReport,
} from '@/lib/firestore/user-aio-reports'

/** Figma Aside SIDEBAR (455:959): 3px rounded purple rail on active rows */
function SidebarActiveRail() {
  return (
    <span
      className="pointer-events-none absolute bottom-1 left-0 top-1 w-[3px] rounded-r-[2px] bg-[hsl(var(--sidebar-active-indicator))]"
      aria-hidden
    />
  )
}

function sidebarPrimaryNavClass(active: boolean, dimmed?: boolean) {
  return cn(
    'relative flex w-full items-center gap-[9px] rounded-none px-4 py-[7px] text-[13px] leading-normal outline-none transition-colors',
    dimmed
      ? cn(
          active
            ? 'bg-sidebar-active-bg/55 font-semibold text-sidebar-active-fg/75'
            : 'cursor-default font-normal text-sidebar-muted/45 opacity-[0.52]'
        )
      : cn(
          'focus-visible:ring-2 focus-visible:ring-sidebar-active-fg/35 focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar',
          active
            ? 'bg-sidebar-active-bg font-semibold text-sidebar-active-fg'
            : 'font-normal text-sidebar-muted hover:bg-sidebar-accent hover:text-sidebar-foreground'
        )
  )
}

function sidebarNestedNavClass(active: boolean, dimmed?: boolean) {
  return cn(
    'relative flex w-full items-center gap-[9px] rounded-none py-[7px] pl-9 pr-4 text-2xs leading-normal outline-none transition-colors',
    dimmed
      ? cn(
          active
            ? 'bg-sidebar-active-bg/55 font-semibold text-sidebar-active-fg/75'
            : 'cursor-default font-normal text-sidebar-muted/45 opacity-[0.52]'
        )
      : cn(
          'focus-visible:ring-2 focus-visible:ring-sidebar-active-fg/35 focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar',
          active
            ? 'bg-sidebar-active-bg font-semibold text-sidebar-active-fg'
            : 'font-normal text-sidebar-muted hover:bg-sidebar-accent hover:text-sidebar-foreground'
        )
  )
}

const sidebarWorkspaceTriggerClass =
  'flex h-auto min-h-0 w-full items-center gap-2 rounded-lg border border-sidebar-border bg-[hsl(var(--sidebar-switcher-bg))] px-[11px] py-[9px] text-[13px] font-normal leading-normal text-sidebar-foreground shadow-none [&>span]:line-clamp-1 data-[placeholder]:text-sidebar-muted'

const sidebarLogoSlotClass =
  'block h-[var(--sidebar-logo-frame-height)] w-[var(--sidebar-logo-frame-width)] max-w-full overflow-hidden'

const readReportOwnerId = (report: { userId?: unknown } | null | undefined): string | null => {
  const raw = report?.userId
  if (typeof raw !== 'string') return null
  const trimmed = raw.trim()
  return trimmed || null
}

function SidebarPrimaryNavLink({
  active,
  href,
  icon: Icon,
  children,
  dimmed,
}: {
  active: boolean
  href: string
  icon: ComponentType<{ className?: string }>
  children: ReactNode
  dimmed?: boolean
}) {
  const inner = (
    <>
      {active ? <SidebarActiveRail /> : null}
      <Icon className="relative z-[1] h-4 w-4 shrink-0" aria-hidden />
      <span className="relative z-[1] min-w-0 flex-1 truncate">{children}</span>
    </>
  )

  if (dimmed) {
    return (
      <span className={sidebarPrimaryNavClass(active, dimmed)}>
        {inner}
      </span>
    )
  }

  return (
    <Link
      href={href}
      className={sidebarPrimaryNavClass(active, dimmed)}
      aria-current={active ? 'page' : undefined}
    >
      {inner}
    </Link>
  )
}

function SidebarNestedNavLink({
  active,
  href,
  children,
  dimmed,
}: {
  active: boolean
  href: string
  children: ReactNode
  dimmed?: boolean
}) {
  const inner = (
    <>
      {active ? <SidebarActiveRail /> : null}
      <span className="relative z-[1] min-w-0 flex-1 truncate">{children}</span>
    </>
  )

  if (dimmed) {
    return (
      <span className={sidebarNestedNavClass(active, dimmed)}>
        {inner}
      </span>
    )
  }

  return (
    <Link
      href={href}
      className={sidebarNestedNavClass(active, dimmed)}
      aria-current={active ? 'page' : undefined}
    >
      {inner}
    </Link>
  )
}

function SidebarV1ExpandableGroup({
  pathname,
  reportId,
  title,
  icon: Icon,
  parentPathSegment,
  folded,
  onToggleFolded,
  buildHref = (path) => path,
  children,
}: {
  pathname: string | null | undefined
  reportId: string
  title: string
  icon: ComponentType<{ className?: string }>
  /** e.g. `/ai-visibility` — joined after `/ai-overview-analysis/{reportId}` */
  parentPathSegment: string
  folded: boolean
  onToggleFolded: () => void
  /** Preserve v1 topbar query params when navigating between report sections. */
  buildHref?: (path: string) => string
  children: ReactNode
}) {
  const base = `/ai-overview-analysis/${reportId}`
  const parentHref = buildHref(`${base}${parentPathSegment}`)
  const normPath = pathname?.replace(/\/$/, "") ?? ""
  const normParent = parentHref.replace(/\/$/, "")
  const inSection =
    normPath === normParent ||
    normPath.startsWith(`${normParent}/`)
  const parentActive = inSection

  return (
    <div className="flex flex-col">
      <div className="flex w-full min-w-0 items-stretch">
        <Link href={parentHref} className={cn(sidebarPrimaryNavClass(parentActive), 'min-w-0 flex-1')}>
          {parentActive ? <SidebarActiveRail /> : null}
          <Icon className="relative z-[1] h-4 w-4 shrink-0" aria-hidden />
          <span className="relative z-[1] min-w-0 flex-1 truncate">{title}</span>
        </Link>
        <button
          type="button"
          className="flex w-9 shrink-0 items-center justify-center rounded-none text-sidebar-muted outline-none transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground focus-visible:ring-2 focus-visible:ring-sidebar-active-fg/35 focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar"
          aria-expanded={!folded}
          aria-label={`${folded ? "Expand" : "Collapse"} ${title} submenu`}
          onClick={onToggleFolded}
        >
          <ChevronDown
            className={cn("h-4 w-4 shrink-0 transition-transform", folded ? "-rotate-90" : "rotate-0")}
            aria-hidden
          />
        </button>
      </div>
      {!folded ? <div className="flex flex-col">{children}</div> : null}
    </div>
  )
}

interface PublicAIOReport {
  id: string;
  topic: string;
  website_url?: string;
  date_created: any;
  userId?: string;
  public: boolean;
}


interface SidebarProps {
  isCollapsedParam?: boolean
}

export function Sidebar({ isCollapsedParam }: SidebarProps) {
  const { isCollapsed, setIsCollapsed } = useSidebar()
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const [, setPublicReports] = useState<PublicAIOReport[]>([])
  const [, setIsLoading] = useState(true)
  const [ownedAioReports, setOwnedAioReports] = useState<UserAioReport[]>([])
  const [sharedAioReports, setSharedAioReports] = useState<UserAioReport[]>([])
  const [isLoadingUserReports, setIsLoadingUserReports] = useState(false)
  const [reportNavReady, setReportNavReady] = useState(false)
  const [reportSchemaVersion, setReportSchemaVersion] = useState<number | null>(null)
  const [currentReportOwnerId, setCurrentReportOwnerId] = useState<string | null>(null)
  const [v1SectionFolded, setV1SectionFolded] = useState({
    aiVisibility: false,
    content: false,
  })

  const { user } = useAuthContext()
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const brandedBootstrap = useBrandedReportBootstrap()
  const brandedLogoUrl = brandedBootstrap?.branding?.logo_url?.trim() || null
  const isBrandedReportView = brandedBootstrap != null

  const normPathname = pathname?.replace(/\/$/, "") ?? ""

  const reportNavHref = (path: string) =>
    appendV1ReportFilterQueryToPath(path, searchParams)

  const currentReportId = pathname?.startsWith('/ai-overview-analysis/')
    ? pathname.replace(/^\/ai-overview-analysis\//, '').split('/')[0] || null
    : null
  const effectiveReportId =
    currentReportId ??
    (typeof brandedBootstrap?.report?.id === 'string' ? brandedBootstrap.report.id : null)
  const currentSection = pathname?.includes('/action-plan')
    ? 'action-plan'
    : pathname?.includes('/website-analysis')
      ? 'website-analysis'
      : pathname?.includes('/citation-analysis') || pathname?.includes('/citations/table-view')
        ? 'citation-analysis'
        : pathname?.includes('/citations')
          ? 'citations'
          : null
  const currentWebsiteAnalysisTab = pathname?.includes('/website-analysis/content-gap-analysis')
    ? 'content-gap-analysis'
    : pathname?.includes('/website-analysis/content-opportunities-analysis')
      ? 'content-opportunities-analysis'
      : pathname?.includes('/website-analysis')
        ? 'overview'
        : null
  const currentCitationsTab = pathname?.includes('/citations/brands-citation')
    ? 'brands-citation'
    : pathname?.includes('/citations/table-view')
      ? 'table-view'
      : pathname?.includes('/citations/card-view')
        ? 'card-view'
        : pathname?.includes('/citations')
          ? 'summary'
          : null

  useEffect(() => {
    setIsCollapsed(isCollapsedParam || false)
  }, [isCollapsedParam, setIsCollapsed])

  useEffect(() => {
    if (!effectiveReportId) {
      setReportNavReady(false)
      setReportSchemaVersion(null)
      setCurrentReportOwnerId(null)
      return
    }

    if (isBrandedReportView && brandedBootstrap?.report) {
      const r = brandedBootstrap.report as { version?: unknown; userId?: unknown }
      setReportSchemaVersion(typeof r.version === 'number' ? r.version : null)
      setCurrentReportOwnerId(readReportOwnerId(r))
      setReportNavReady(true)
      return
    }

    let cancelled = false
    setReportNavReady(false)

    const loadVersion = async () => {
      try {
        const { doc, getDoc } = await import('firebase/firestore')
        const { getFirestoreInstance } = await import('@/lib/firestore/firebase-singleton')
        const snap = await getDoc(doc(getFirestoreInstance(), 'aio_analysis_report', effectiveReportId))
        if (cancelled) return
        const data = snap.exists() ? snap.data() : undefined
        const raw = data?.version
        setReportSchemaVersion(typeof raw === 'number' ? raw : null)
        setCurrentReportOwnerId(readReportOwnerId(data))
      } catch {
        if (!cancelled) {
          setReportSchemaVersion(null)
          setCurrentReportOwnerId(null)
        }
      } finally {
        if (!cancelled) setReportNavReady(true)
      }
    }

    void loadVersion()
    return () => {
      cancelled = true
    }
  }, [effectiveReportId, isBrandedReportView, brandedBootstrap?.report])

  useEffect(() => {
    if (!effectiveReportId || !pathname) return
    const base = `/ai-overview-analysis/${effectiveReportId}`
    setV1SectionFolded((f) => {
      const next = { ...f }
      if (pathname.startsWith(`${base}/ai-visibility`)) next.aiVisibility = false
      if (pathname.startsWith(`${base}/content`)) next.content = false
      return next
    })
  }, [pathname, effectiveReportId])
  
  useEffect(() => {
    const fetchPublicReports = async () => {
      try {
        setIsLoading(true)
        
        // Import Firestore functions
        const { collection, query, where, orderBy, getDocs, limit } = await import('firebase/firestore')
        const { getFirestoreInstance } = await import('@/lib/firestore/firebase-singleton')
        
        const db = getFirestoreInstance()
        
        // Query for public aio_analysis_report documents
        const publicReportsQuery = query(
          collection(db, 'aio_analysis_report'),
          where('public', '==', true),
          where('full_report_available', '==', true),
          orderBy('date_created', 'desc'),
          limit(50)
        )
        
        const snapshot = await getDocs(publicReportsQuery)
        const reports = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as PublicAIOReport[]
        
        setPublicReports(reports)
      } catch (error) {
        logger.error(error instanceof Error ? error : new Error(String(error)), 'Error fetching public AI Overview reports:', [], user?.uid || undefined)
      } finally {
        setIsLoading(false)
      }
    }

    fetchPublicReports()
  }, [user])

  // Fetch owned and shared AIO reports
  useEffect(() => {
    const fetchUserAIOReports = async () => {
      if (!user?.uid || isBrandedReportView) {
        setOwnedAioReports([])
        setSharedAioReports([])
        return
      }

      try {
        setIsLoadingUserReports(true)
        const { owned, shared } = await fetchUserAioReports(user.uid)
        setOwnedAioReports(owned)
        setSharedAioReports(shared)
      } catch (error) {
        logger.error(error instanceof Error ? error : new Error(String(error)), 'Error fetching user AIO reports:', [], user?.uid || undefined)
      } finally {
        setIsLoadingUserReports(false)
      }
    }

    fetchUserAIOReports()
  }, [user, isBrandedReportView])

  const showAccessNav =
    !isBrandedReportView &&
    Boolean(user?.uid && currentReportOwnerId && currentReportOwnerId === user.uid)

  const handleSelectReport = (reportId: string) => {
    router.push(`/ai-overview-analysis/${reportId}`)
  }

  const handleGoogleSignIn = async () => {
    try {
      console.log('🔄 Starting Google Sign-In process from sidebar...')
      await googleSignIn()
      console.log('✅ Google Sign-In successful from sidebar')
    } catch (error) {
      console.error('❌ Google Sign-In failed from sidebar:', error)
      logger.error(error instanceof Error ? error : new Error(String(error)), 'Failed to sign in:', [], user?.uid || undefined);
    }
  }

  return (
    <>
      {/* Mobile Menu Button */}
      <Button
        variant="outline"
        size="icon"
        className="fixed left-4 bottom-4 h-10 w-10 rounded-full border bg-background shadow-md md:hidden z-50"
        onClick={() => setIsMobileOpen(true)}
      >
        <Menu className="h-4 w-4" />
      </Button>

      <div 
        className={`
          fixed inset-0 bg-black/80 z-40 transition-opacity duration-300 md:hidden
          ${isMobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}
        onClick={() => setIsMobileOpen(false)}
      />

      <div 
        className={cn(
          'fixed left-0 top-0 z-50 flex h-[100dvh] flex-col overflow-hidden border-r border-sidebar-border bg-sidebar transition-all duration-300 md:h-screen',
          'w-[var(--sidebar-width-expanded)] p-0',
          isMobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
          isCollapsed
            ? 'md:relative md:w-[var(--sidebar-width-collapsed)]'
            : 'md:relative md:w-[var(--sidebar-width-expanded)]'
        )}
      >
        {/* Mobile Close Button */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-2 top-2 h-6 w-6 md:hidden"
          onClick={() => setIsMobileOpen(false)}
        >
          <X className="h-4 w-4" />
        </Button>

        {/* Desktop Toggle Button */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute -right-3 top-6 hidden h-7 w-7 rounded-full border border-sidebar-border bg-sidebar text-sidebar-muted shadow-sm hover:bg-sidebar-accent hover:text-sidebar-foreground md:flex"
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          {isCollapsed ? (
            <ChevronRight className="h-4 w-4 -translate-x-[5px]" />
          ) : (
            <ChevronLeft className="h-4 w-4 -translate-x-[5px]" />
          )}
        </Button>

        <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto">
        {/* Logo — customer logo replaces SEOPulse when branded (Figma slot 18×96px) */}
        <div className="shrink-0 border-b border-[hsl(var(--sidebar-header-rule))] px-4 pb-[15px] pt-[18px] md:pt-[18px]">
          <Link
            href={
              isBrandedReportView && effectiveReportId
                ? `/ai-overview-analysis/${effectiveReportId}`
                : '/'
            }
            className="block"
          >
            <span className={sidebarLogoSlotClass}>
              {brandedLogoUrl ? (
                <img
                  src={brandedLogoUrl}
                  alt=""
                  className="h-full w-full object-contain object-left"
                  width={96}
                  height={18}
                />
              ) : (
                <SeoPulseSidebarLogoMark className="h-full w-full [&>svg]:h-full [&>svg]:w-full" />
              )}
            </span>
          </Link>
        </div>

        {/* Primary nav + report sections */}
        <div className="flex shrink-0 flex-col">
            {!isBrandedReportView && user ? (
              <div className={`mb-3 shrink-0 px-2.5 pt-2.5 ${isCollapsed ? 'md:hidden' : ''}`}>
                <SidebarAioReportSwitcher
                  currentReportId={currentReportId}
                  ownedReports={ownedAioReports}
                  sharedReports={sharedAioReports}
                  isLoading={isLoadingUserReports}
                  triggerClassName={sidebarWorkspaceTriggerClass}
                  onSelectReport={handleSelectReport}
                />
              </div>
            ) : null}

            {/* Report sections (v1 IA vs legacy) */}
            {effectiveReportId && (
              <div className={`mb-4 shrink-0 ${isCollapsed ? 'md:hidden' : ''}`}>
                {!reportNavReady ? (
                  <div className="flex flex-col gap-2 px-1 py-2" aria-busy="true" aria-live="polite">
                    <Skeleton className="h-10 w-full rounded-lg bg-sidebar-accent/60" />
                    <Skeleton className="ml-2 h-8 w-[92%] rounded-md bg-sidebar-accent/50" />
                    <Skeleton className="ml-2 h-8 w-[88%] rounded-md bg-sidebar-accent/50" />
                    <Skeleton className="h-10 w-full rounded-lg bg-sidebar-accent/60" />
                    <Skeleton className="ml-2 h-8 w-[90%] rounded-md bg-sidebar-accent/50" />
                  </div>
                ) : reportSchemaVersion === 1 ? (
                  <nav className="flex flex-col py-2" aria-label="Report sections">
                    <SidebarV1ExpandableGroup
                      pathname={pathname}
                      reportId={effectiveReportId}
                      title="AI Visibility"
                      icon={Eye}
                      parentPathSegment="/ai-visibility"
                      buildHref={reportNavHref}
                      folded={v1SectionFolded.aiVisibility}
                      onToggleFolded={() =>
                        setV1SectionFolded((f) => ({ ...f, aiVisibility: !f.aiVisibility }))
                      }
                    >
                      <SidebarNestedNavLink
                        href={reportNavHref(
                          `/ai-overview-analysis/${effectiveReportId}/ai-visibility/engines`
                        )}
                        active={
                          normPathname ===
                          `/ai-overview-analysis/${effectiveReportId}/ai-visibility/engines`
                        }
                      >
                        Engines
                      </SidebarNestedNavLink>
                      <SidebarNestedNavLink
                        href={reportNavHref(
                          `/ai-overview-analysis/${effectiveReportId}/ai-visibility/categories`
                        )}
                        active={
                          normPathname ===
                          `/ai-overview-analysis/${effectiveReportId}/ai-visibility/categories`
                        }
                      >
                        Categories
                      </SidebarNestedNavLink>
                      <SidebarNestedNavLink
                        href={reportNavHref(
                          `/ai-overview-analysis/${effectiveReportId}/ai-visibility/competitors`
                        )}
                        active={
                          normPathname ===
                          `/ai-overview-analysis/${effectiveReportId}/ai-visibility/competitors`
                        }
                      >
                        Competitors
                      </SidebarNestedNavLink>
                    </SidebarV1ExpandableGroup>
                    <SidebarV1ExpandableGroup
                      pathname={pathname}
                      reportId={effectiveReportId}
                      title="Content"
                      icon={FileText}
                      parentPathSegment="/content"
                      buildHref={reportNavHref}
                      folded={v1SectionFolded.content}
                      onToggleFolded={() =>
                        setV1SectionFolded((f) => ({ ...f, content: !f.content }))
                      }
                    >
                      <SidebarNestedNavLink
                        href={reportNavHref(
                          `/ai-overview-analysis/${effectiveReportId}/content/optimization`
                        )}
                        active={
                          normPathname ===
                          `/ai-overview-analysis/${effectiveReportId}/content/optimization`
                        }
                      >
                        Optimization
                      </SidebarNestedNavLink>
                      <SidebarNestedNavLink
                        href={reportNavHref(
                          `/ai-overview-analysis/${effectiveReportId}/content/gap-analysis`
                        )}
                        active={
                          normPathname ===
                          `/ai-overview-analysis/${effectiveReportId}/content/gap-analysis`
                        }
                      >
                        Gap Analysis
                      </SidebarNestedNavLink>
                      <SidebarNestedNavLink
                        href={reportNavHref(
                          `/ai-overview-analysis/${effectiveReportId}/content/competitor-insights`
                        )}
                        active={
                          normPathname ===
                          `/ai-overview-analysis/${effectiveReportId}/content/competitor-insights`
                        }
                        // dimmed
                      >
                        Competitor Insights
                      </SidebarNestedNavLink>
                      <SidebarNestedNavLink
                        href={reportNavHref(
                          `/ai-overview-analysis/${effectiveReportId}/content/impact-tracker`
                        )}
                        active={
                          normPathname ===
                          `/ai-overview-analysis/${effectiveReportId}/content/impact-tracker`
                        }
                        dimmed
                      >
                        Impact Tracker
                      </SidebarNestedNavLink>
                    </SidebarV1ExpandableGroup>
                    <SidebarPrimaryNavLink
                      href={reportNavHref(
                        `/ai-overview-analysis/${effectiveReportId}/direct-comparison`
                      )}
                      active={
                        normPathname ===
                        `/ai-overview-analysis/${effectiveReportId}/direct-comparison`
                      }
                      icon={GitCompare}
                      dimmed
                    >
                      Direct Comparison
                    </SidebarPrimaryNavLink>
                    <SidebarPrimaryNavLink
                      href={reportNavHref(`/ai-overview-analysis/${effectiveReportId}/technical`)}
                      active={
                        normPathname === `/ai-overview-analysis/${effectiveReportId}/technical`
                      }
                      icon={Wrench}
                      dimmed
                    >
                      Technical
                    </SidebarPrimaryNavLink>
                    <SidebarPrimaryNavLink
                      href={reportNavHref(`/ai-overview-analysis/${effectiveReportId}/sentiment`)}
                      active={
                        normPathname === `/ai-overview-analysis/${effectiveReportId}/sentiment`
                      }
                      icon={Smile}
                      dimmed
                    >
                      Sentiment
                    </SidebarPrimaryNavLink>
                    <SidebarPrimaryNavLink
                      href={reportNavHref(
                        `/ai-overview-analysis/${effectiveReportId}/citation/queries`
                      )}
                      active={
                        normPathname ===
                        `/ai-overview-analysis/${effectiveReportId}/citation/queries`
                      }
                      icon={FileText}
                    >
                      Queries
                    </SidebarPrimaryNavLink>
                    {showAccessNav ? (
                      <SidebarPrimaryNavLink
                        href={reportNavHref(
                          `/ai-overview-analysis/${effectiveReportId}/access`
                        )}
                        active={
                          normPathname ===
                          `/ai-overview-analysis/${effectiveReportId}/access`
                        }
                        icon={Users}
                      >
                        Access
                      </SidebarPrimaryNavLink>
                    ) : null}
                    <SidebarPrimaryNavLink
                      href={reportNavHref(`/ai-overview-analysis/${effectiveReportId}/citation`)}
                      active={
                        normPathname === `/ai-overview-analysis/${effectiveReportId}/citation`
                      }
                      icon={Quote}
                      dimmed
                    >
                      Citation
                    </SidebarPrimaryNavLink>
                  </nav>
                ) : (
                  <nav className="flex flex-col py-2" aria-label="Report sections">
                    <SidebarPrimaryNavLink
                      href={`/ai-overview-analysis/${effectiveReportId}/citations`}
                      active={currentSection === 'citations'}
                      icon={Quote}
                    >
                      Brands Performance
                    </SidebarPrimaryNavLink>
                    {showAccessNav ? (
                      <SidebarPrimaryNavLink
                        href={`/ai-overview-analysis/${effectiveReportId}/access`}
                        active={
                          normPathname ===
                          `/ai-overview-analysis/${effectiveReportId}/access`
                        }
                        icon={Users}
                      >
                        Access
                      </SidebarPrimaryNavLink>
                    ) : null}
                    <div className="flex flex-col" aria-label="Citation analysis views">
                      <SidebarNestedNavLink
                        href={`/ai-overview-analysis/${effectiveReportId}/citations`}
                        active={currentCitationsTab === 'summary'}
                      >
                        Summary
                      </SidebarNestedNavLink>
                      <SidebarNestedNavLink
                        href={`/ai-overview-analysis/${effectiveReportId}/citations/card-view`}
                        active={currentCitationsTab === 'card-view'}
                      >
                        Card View
                      </SidebarNestedNavLink>
                      <SidebarNestedNavLink
                        href={`/ai-overview-analysis/${effectiveReportId}/citations/brands-citation`}
                        active={currentCitationsTab === 'brands-citation'}
                      >
                        Brands &amp; Citation
                      </SidebarNestedNavLink>
                    </div>
                    <SidebarPrimaryNavLink
                      href={`/ai-overview-analysis/${effectiveReportId}/citation-analysis`}
                      active={currentSection === 'citation-analysis'}
                      icon={Quote}
                    >
                      Citation Analysis
                    </SidebarPrimaryNavLink>
                    <div className="flex flex-col" aria-label="Citation analysis overview">
                      <SidebarNestedNavLink
                        href={`/ai-overview-analysis/${effectiveReportId}/citation-analysis`}
                        active={currentSection === 'citation-analysis'}
                      >
                        Overview
                      </SidebarNestedNavLink>
                      <SidebarNestedNavLink
                        href={`/ai-overview-analysis/${effectiveReportId}/citations/table-view`}
                        active={currentCitationsTab === 'table-view'}
                      >
                        Table View
                      </SidebarNestedNavLink>
                    </div>
                    {/* <Link ... Action Plan ... /> */}
                    <SidebarPrimaryNavLink
                      href={`/ai-overview-analysis/${effectiveReportId}/website-analysis`}
                      active={currentSection === 'website-analysis'}
                      icon={Layout}
                    >
                      Website Analysis [Beta]
                    </SidebarPrimaryNavLink>
                    <div className="flex flex-col" aria-label="Website analysis views">
                      <SidebarNestedNavLink
                        href={`/ai-overview-analysis/${effectiveReportId}/website-analysis`}
                        active={currentWebsiteAnalysisTab === 'overview'}
                      >
                        Overview
                      </SidebarNestedNavLink>
                      <SidebarNestedNavLink
                        href={`/ai-overview-analysis/${effectiveReportId}/website-analysis/content-gap-analysis`}
                        active={currentWebsiteAnalysisTab === 'content-gap-analysis'}
                      >
                        Content Gap Analysis
                      </SidebarNestedNavLink>
                      <SidebarNestedNavLink
                        href={`/ai-overview-analysis/${effectiveReportId}/website-analysis/content-opportunities-analysis`}
                        active={
                          currentWebsiteAnalysisTab === 'content-opportunities-analysis'
                        }
                      >
                        Content Opportunities Analysis
                      </SidebarNestedNavLink>
                    </div>
                  </nav>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Bottom Section */}
        {!isBrandedReportView && (
          <div
            className={cn(
              'mt-auto shrink-0 space-y-2 border-t border-[hsl(var(--sidebar-header-rule))] px-[14px] pb-3 pt-[13px]',
              isCollapsed ? 'md:hidden' : ''
            )}
          >
            {!user || !user.idToken ? (
              <Button 
                variant="outline" 
                onClick={handleGoogleSignIn}
                className="h-auto w-full rounded-lg border-sidebar-border bg-sidebar px-[11px] py-[9px] text-[13px] font-normal text-sidebar-foreground hover:bg-sidebar-accent"
              >
                <GoogleIcon className="mr-2 h-4 w-4" />
                Sign in with Google
              </Button>
            ) : (
              <div className="flex items-center justify-between gap-2 rounded-lg bg-sidebar-active-bg px-2 py-2">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <img 
                    src={user.photoURL || ''} 
                    alt={user.displayName || 'User'} 
                    className="h-7 w-7 shrink-0 rounded-full object-cover"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-semibold text-sidebar-foreground">
                      {user.displayName || 'User'}
                    </div>
                    {user.email ? (
                      <div className="truncate text-[9px] leading-tight text-[hsl(var(--sidebar-footer-meta))]">
                        {user.email}
                      </div>
                    ) : null}
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-sidebar-muted hover:bg-sidebar-accent hover:text-sidebar-foreground">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem asChild>
                      <Link href="/settings">Settings</Link>
                    </DropdownMenuItem>
                    {/* {currentReportId &&
                    ownedAioReports.some((r) => r.id === currentReportId) ? (
                      <DropdownMenuItem asChild>
                        <Link href={`/ai-overview-analysis/${currentReportId}/access`}>
                          Report access
                        </Link>
                      </DropdownMenuItem>
                    ) : null} */}
                    <DropdownMenuItem asChild>
                      <a data-canny-link 
                        href="https://seopulse.canny.io/report-feature-issue" 
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Give feedback
                      </a>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
} 